// LF360-1830 — options for the "trigger field" picker on a personally assigned
// relative task.
//
// A relative task takes its time from one of the subject's profile time fields, so
// a field THIS subject has never answered cannot schedule anything, and the backend
// refuses an assignment configured that way with
// TRIGGER_SUBJECT_PROFILE_VALUE_REQUIRED. Offering it anyway means the admin only
// finds out on save, so such a field is shown disabled, with the reason, instead.
//
// LF360-1940: that refusal covers a reminder chosen HERE, and only that. One
// inherited from the task is taken and simply not sent until the subject answers,
// because refusing it would make whether a task can be assigned at all depend on
// when its relative default was set. The `disabled` flag below is what marks that
// case too, and ReminderList turns it into the note on the reminder itself.

const hasValue = (entry) => {
  if (!entry) return false;
  const value = entry.value;
  if (Array.isArray(value)) return value.some((v) => v !== null && v !== undefined && v !== "");
  return value !== null && value !== undefined && value !== "";
};

/**
 * @param {Array} timeProfileFields the project's `message:time` profile fields
 * @param {Array} subjectCustomFields the subject's resolved values, as the subject
 *   endpoint returns them (`[{ profileFieldId, value }]`)
 */
export const triggerFieldOptions = (timeProfileFields = [], subjectCustomFields = null) => {
  // Fail open: null means the subject's values could not be loaded, which is not
  // the same as "not answered". Disabling everything then would leave the admin
  // unable to pick any field at all — worse than the error this avoids.
  const known = Array.isArray(subjectCustomFields);
  const answered = new Set(
    (known ? subjectCustomFields : []).filter(hasValue).map((entry) => entry.profileFieldId)
  );

  return (Array.isArray(timeProfileFields) ? timeProfileFields : []).map((field) => {
    const usable = !known || answered.has(field.id);

    return {
      value: field.id,
      label: usable ? field.title : `${field.title} — not answered yet`,
      // The question on its own, for callers that have to name it in a sentence
      // rather than in a list — a label carrying its own explanation cannot be
      // put inside another one (LF360-1940).
      title: field.title,
      disabled: !usable,
    };
  });
};

/** True when no time field can schedule anything for this subject. */
export const hasUsableTriggerField = (timeProfileFields, subjectCustomFields) =>
  triggerFieldOptions(timeProfileFields, subjectCustomFields).some((o) => !o.disabled);
